/**
 * Drawing sheet overlay — renders the generated SheetModel as SVG (crisp
 * at any zoom) with export to DXF and PDF. The sheet model is Y-up mm;
 * SVG is Y-down, so everything renders through one flip transform.
 */

import { appendAlignedDim, viewAt } from '@core/drawing/layout';
import { sheetToDxf } from '@core/io/dxf';
import { sheetToPdf } from '@core/io/pdf';
import type { SheetModel } from '@core/drawing/layout';
import type { App } from '../app';

function download(blob: Blob, filename: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

function sheetSvg(m: SheetModel): string {
  const path = (segs: number[]): string => {
    let d = '';
    for (let i = 0; i + 3 < segs.length; i += 4) {
      d += `M${r(segs[i]!)} ${r(segs[i + 1]!)}L${r(segs[i + 2]!)} ${r(segs[i + 3]!)}`;
    }
    return d;
  };
  const layer = (segs: number[], style: string): string =>
    segs.length ? `<path d="${path(segs)}" fill="none" ${style}/>` : '';
  const texts = m.texts
    .map((t) => {
      const anchor = t.anchor === 'middle' ? 'middle' : t.anchor === 'end' ? 'end' : 'start';
      // Text needs its own flip so glyphs aren't mirrored.
      return `<text x="${r(t.p[0])}" y="${r(-t.p[1])}" transform="scale(1,-1)" font-size="${t.size}" text-anchor="${anchor}" font-family="Helvetica, Arial, sans-serif" fill="#1a1d23">${esc(t.text)}</text>`;
    })
    .join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${m.width} ${m.height}">` +
    `<rect width="${m.width}" height="${m.height}" fill="#fcfcf8"/>` +
    `<g transform="translate(0,${m.height}) scale(1,-1)">` +
    layer(m.frame, 'stroke="#39415a" stroke-width="0.35"') +
    layer(m.hatch, 'stroke="#9aa" stroke-width="0.12"') +
    layer(m.hidden, 'stroke="#777" stroke-width="0.18" stroke-dasharray="1.6 1.1"') +
    layer(m.visible, 'stroke="#111" stroke-width="0.35"') +
    layer(m.section, 'stroke="#b32" stroke-width="0.5"') +
    layer(m.dims, 'stroke="#1a5a9a" stroke-width="0.16"') +
    texts +
    '</g></svg>'
  );
}

const r = (v: number): number => Math.round(v * 100) / 100;
const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function mountDrawingPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-drawing';
  panel.style.display = 'none';
  host.appendChild(panel);

  /** Dimension-placement mode: waiting for the first/second pick. */
  let dimMode = false;
  let firstPick: [number, number] | null = null;

  /** Client → sheet mm (Y up), snapped to the nearest drawn endpoint. */
  const pickPoint = (svg: SVGSVGElement, e: MouseEvent): [number, number] | null => {
    const m = app.sheet;
    if (!m) return null;
    const r = svg.getBoundingClientRect();
    const sx = ((e.clientX - r.left) / r.width) * m.width;
    const sy = m.height - ((e.clientY - r.top) / r.height) * m.height;
    let best: [number, number] = [sx, sy];
    let bestD = 3; // mm snap radius on the sheet
    for (const bucket of [m.visible, m.section]) {
      for (let i = 0; i + 1 < bucket.length; i += 2) {
        const d = Math.hypot(bucket[i]! - sx, bucket[i + 1]! - sy);
        if (d < bestD) {
          bestD = d;
          best = [bucket[i]!, bucket[i + 1]!];
        }
      }
    }
    return best;
  };

  const render = (): void => {
    panel.style.display = app.drawingOpen ? 'flex' : 'none';
    if (!app.drawingOpen) return;
    panel.innerHTML = '';

    const bar = document.createElement('div');
    bar.className = 'drawing-bar';
    const title = document.createElement('span');
    title.textContent = `Drawing — ${app.doc.name}`;
    bar.appendChild(title);
    const mk = (label: string, fn: () => void, primary = false, active = false): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      if (primary) b.className = 'primary';
      if (active) b.classList.add('active-mode');
      b.addEventListener('click', fn);
      bar.appendChild(b);
      return b;
    };

    // Sheet options.
    const sizeSel = document.createElement('select');
    for (const s of ['A3', 'A4']) {
      const o = document.createElement('option');
      o.value = s;
      o.textContent = s;
      sizeSel.appendChild(o);
    }
    sizeSel.value = app.sheetOptions.size;
    sizeSel.addEventListener('change', () => {
      app.sheetOptions.size = sizeSel.value as 'A3' | 'A4';
      app.customDims = []; // sheet coords no longer match
      void app.generateDrawing();
    });
    bar.appendChild(sizeSel);
    const scaleSel = document.createElement('select');
    for (const [v, label] of [
      ['auto', 'scale: auto'], ['2', '2:1'], ['1', '1:1'],
      ['0.5', '1:2'], ['0.2', '1:5'], ['0.1', '1:10'],
    ] as const) {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = label;
      scaleSel.appendChild(o);
    }
    scaleSel.value = app.sheetOptions.scale === 'auto' ? 'auto' : String(app.sheetOptions.scale);
    scaleSel.addEventListener('change', () => {
      app.sheetOptions.scale = scaleSel.value === 'auto' ? 'auto' : Number(scaleSel.value);
      app.customDims = [];
      void app.generateDrawing();
    });
    bar.appendChild(scaleSel);

    mk(
      dimMode ? (firstPick ? 'Dimension: pick 2nd point' : 'Dimension: pick 1st point') : 'Dimension',
      () => {
        dimMode = !dimMode;
        firstPick = null;
        render();
      },
      false,
      dimMode,
    );
    if (app.customDims.length > 0) {
      mk(`Clear dims (${app.customDims.length})`, () => {
        app.customDims = [];
        void app.generateDrawing();
      });
    }
    mk('Regenerate', () => void app.generateDrawing());
    mk('DXF', () => {
      if (app.sheet) {
        download(
          new Blob([sheetToDxf(app.sheet)], { type: 'application/dxf' }),
          `${app.doc.name.replace(/[^\w\- ]+/g, '') || 'drawing'}.dxf`,
        );
      }
    });
    mk('PDF', () => {
      if (app.sheet) {
        download(sheetToPdf(app.sheet), `${app.doc.name.replace(/[^\w\- ]+/g, '') || 'drawing'}.pdf`);
      }
    });
    mk('Close', () => app.toggleDrawing(), true);
    panel.appendChild(bar);

    const sheetHost = document.createElement('div');
    sheetHost.className = 'drawing-sheet';
    if (app.sheetBusy) {
      sheetHost.innerHTML = '<div class="drawing-status">Projecting views…</div>';
    } else if (app.sheet) {
      sheetHost.innerHTML = sheetSvg(app.sheet);
      const svg = sheetHost.querySelector('svg')!;
      if (dimMode) svg.style.cursor = 'crosshair';
      svg.addEventListener('click', (e) => {
        if (!dimMode || !app.sheet) return;
        const p = pickPoint(svg, e);
        if (!p) return;
        if (!firstPick) {
          firstPick = p;
          render();
          return;
        }
        // Both picks must land in the SAME view to read a true model value.
        const va = viewAt(app.sheet, firstPick);
        const vb = viewAt(app.sheet, p);
        if (!va || va !== vb) {
          app.setIoStatus('Pick both points inside ONE view');
          firstPick = null;
          render();
          return;
        }
        const value = Math.hypot(p[0] - firstPick[0], p[1] - firstPick[1]) / va.scale;
        const dim = {
          a: firstPick,
          b: p,
          text: String(Math.round(value * 100) / 100),
        };
        app.customDims.push(dim);
        appendAlignedDim(app.sheet, dim.a, dim.b, dim.text);
        firstPick = null;
        render();
      });
      const note = document.createElement('div');
      note.className = 'drawing-note';
      note.textContent = dimMode
        ? 'Dimension mode: click two points (snaps to line ends) — value reads TRUE model mm'
        : app.section
          ? 'Includes the active section view · auto dims: overall + hole ⌀ · "Dimension" adds your own'
          : 'Tip: turn on the Section view first to add a SECTION view · "Dimension" adds custom dims';
      sheetHost.appendChild(note);
    } else {
      sheetHost.innerHTML = '<div class="drawing-status">No sheet yet — Regenerate</div>';
    }
    panel.appendChild(sheetHost);
  };

  app.onUI(render);
  render();
}
